import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  Box,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  Description as DescriptionIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material'
import { Activity } from '../../types/dashboard'

interface RecentActivityProps {
  activities: Activity[]
}

export const RecentActivity: React.FC<RecentActivityProps> = ({ activities }) => {
  const { t } = useTranslation('dashboard')

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'test_completed':
        return <CheckCircleIcon />
      case 'user_registered':
        return <PersonAddIcon />
      case 'rule_created':
        return <DescriptionIcon />
      case 'enrollment_created':
        return <QuizIcon />
      default:
        return <QuizIcon />
    }
  }

  const getActivityMessage = (activity: Activity): string => {
    switch (activity.type) {
      case 'test_completed':
        if (activity.testTitle && activity.score !== undefined) {
          return t('recentActivity.testCompleted', {
            testTitle: activity.testTitle,
            score: activity.score,
          })
        }
        if (activity.testTitle) {
          return t('recentActivity.testCompletedNoScore', { testTitle: activity.testTitle })
        }
        return activity.message || ''
      case 'user_registered':
        return t('recentActivity.userRegistered')
      case 'rule_created':
        return t('recentActivity.ruleCreated')
      case 'enrollment_created':
        if (activity.testTitle && activity.count) {
          return t('recentActivity.enrollmentCreated', {
            testTitle: activity.testTitle,
            count: activity.count,
          })
        }
        return activity.message || ''
      default:
        return activity.message || ''
    }
  }

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'test_completed':
        return 'success'
      case 'user_registered':
        return 'primary'
      case 'rule_created':
        return 'secondary'
      case 'enrollment_created':
        return 'info'
      default:
        return 'default'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    // マイナス値の場合（未来の日時）は「たった今」として表示
    if (diffInMinutes < 0) {
      return t('recentActivity.minutesAgo', { minutes: 0 })
    } else if (diffInMinutes < 60) {
      return t('recentActivity.minutesAgo', { minutes: diffInMinutes })
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60)
      return t('recentActivity.hoursAgo', { hours })
    } else {
      const days = Math.floor(diffInMinutes / 1440)
      return t('recentActivity.daysAgo', { days })
    }
  }

  return (
    <Card>
      <CardHeader title={t('recentActivity.title')} />
      <CardContent sx={{ pt: 0 }}>
        {activities.length === 0 ? (
          <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
            {t('recentActivity.noActivity')}
          </Typography>
        ) : (
          <List disablePadding>
            {activities.map((activity, index) => (
              <ListItem
                key={activity.id}
                sx={{
                  px: 0,
                  borderBottom: index < activities.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: `${getActivityColor(activity.type)}.main`,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {getActivityIcon(activity.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" component="span">
                        {getActivityMessage(activity)}
                      </Typography>
                      {activity.user && (
                        <Chip label={activity.user.name} size="small" variant="outlined" />
                      )}
                    </Box>
                  }
                  secondary={formatTimestamp(activity.timestamp)}
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  )
}
